<script setup>
import { ref, computed } from 'vue';
import { usePlatform, subdomain } from '/app/composables/platform';
import { config } from '/app/config';
import { Icon } from '@bignerve/ui-core'
import Storage from '/app/utils/storage';

defineProps({
    placement: {
        type: String,
        default: 'bottom',
    },
});

const STORAGE_KEY = `challenge:creation-flow:subplatform-notice:${subdomain}`;

const { isSubplatform, contextPlatform } = usePlatform();

const dismissed = ref(Storage.get(STORAGE_KEY, false));

const platformTitle = computed(() => {
    return contextPlatform.value?.title || contextPlatform.value?.name || subdomain;
});

const mainPlatformUrl = computed(() => {
    return new URL('/challenges/create', config.app.baseURL).href;
});

const shouldShow = computed(() => {
    return isSubplatform.value && !dismissed.value;
});

const dismiss = () => {
    dismissed.value = true;
    Storage.set(STORAGE_KEY, true);
};
</script>

<template>
    <VTooltip v-if="isSubplatform" :shown="shouldShow" :triggers="[]" theme="danger-alert"
        :placement="placement" :auto-hide="false" container="body" :arrow-padding="0"
        :class="{ 'relative z-[10000]': shouldShow }">
        <slot />
        <template #popper>
            <div class="grid gap-2 text-white max-w-[26rem] p-4 text-left">
                <h3 class="font-bold text-lg m-0">
                    {{ $t('pages.challenges.create.subplatform-notice.title', { platform: platformTitle }) }}
                </h3>
                <i18n-t keypath="pages.challenges.create.subplatform-notice.description" tag="p" class="m-0">
                    <template #link>
                        <a :href="mainPlatformUrl" class="text-white font-bold underline hover:text-white/90">BigNerve</a>
                    </template>
                </i18n-t>
                <div class="flex justify-end">
                    <button type="button"
                        class="border border-white rounded-md px-4 py-1.5 font-bold hover:bg-white/10 transition-colors"
                        @click="dismiss">
                        {{ $t('common.go-it') }} <Icon as="alright" />
                    </button>
                </div>
            </div>
        </template>
    </VTooltip>
    <slot v-else />

    <!-- blocks interaction with the rest of the page until the notice is dismissed;
         the popper (z-index 10000) stays above and remains clickable -->
    <Teleport to="body">
        <div v-if="shouldShow" class="fixed inset-0 z-[9999] bg-black/40" aria-hidden="true" />
    </Teleport>
</template>
